import * as React from 'react'
import { ChevronLeft } from 'lucide-react'
import { Badge as ViewOnlyBadge } from '@/components/ui/badge'
import { useViewOnly, useViewOnlyBadge } from './app-shell'

export interface DetailPageProps {
  title: string
  subtitle?: React.ReactNode
  /** A type badge or status pill shown beside the title. */
  badge?: React.ReactNode
  /** Right-aligned actions (Edit, Archive, …). */
  actions?: React.ReactNode
  /** Optional disabled equivalents for a read-only viewer. */
  readOnlyActions?: React.ReactNode
  backHref?: string
  backLabel?: string
  onBack?: () => void
  /** The record body — typically a set of Tabs (Profile / Affiliations / History). */
  children: React.ReactNode
}

/**
 * The archetype for a single record (a Person, an Organization, an Event): a
 * titled header with a type badge and actions, over a tabbed body.
 */
export function DetailPage({
  title,
  subtitle,
  badge,
  actions,
  readOnlyActions,
  backHref,
  backLabel = 'Back',
  onBack,
  children,
}: DetailPageProps) {
  const viewOnly = useViewOnly()
  const showViewOnlyBadge = useViewOnlyBadge()
  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-6 sm:px-8 sm:py-8">
      {backHref && (
        <a
          href={backHref}
          onClick={(event) => {
            if (!onBack) return

            event.preventDefault()
            onBack()
          }}
          className="mb-4 inline-flex items-center gap-1 text-body-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
          {backLabel}
        </a>
      )}
      <header className="flex flex-col gap-4 pb-6 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-heading-sm font-semibold text-foreground">{title}</h1>
            {badge}
          </div>
          {subtitle && <p className="text-body-sm text-muted-foreground">{subtitle}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {showViewOnlyBadge && <ViewOnlyBadge variant="info">View only</ViewOnlyBadge>}
          {viewOnly ? readOnlyActions : actions}
        </div>
      </header>
      {children}
    </div>
  )
}
